/**
 * This functionality was cloned from `platform/packages/technology-innovation/charlie-table/src/shared-hooks/use-infinite-scroll.ts`
 * in the absence of any Atlaskit component that could do the same thing.
 */

import { useCallback, useRef } from 'react';

export interface UseInfiniteScrollOptions {
	onScrollTo: () => void;
	rootMargin?: string;
	threshold?: number | number[];
}

/**
 * Hook for implementing infinite scroll functionality using Intersection Observer API
 *
 * @param options Configuration options for the infinite scroll behavior
 * @returns A ref callback to attach to the element that should trigger loading when visible
 */
export const useInfiniteScroll = ({
	onScrollTo,
	rootMargin = '0px 0px 100px 0px', // Trigger 100px before element comes into view
	threshold = 0,
}: UseInfiniteScrollOptions): ((element: Element | null) => void) => {
	const observerRef = useRef<IntersectionObserver | null>(null);

	const refCallback = useCallback(
		(element: Element | null) => {
			// Cleanup previous observer
			if (observerRef.current) {
				observerRef.current.disconnect();
				observerRef.current = null;
			}

			if (!element) {
				return;
			}

			// Create new observer
			observerRef.current = new IntersectionObserver(
				(entries) => {
					const [entry] = entries;
					if (entry.isIntersecting) {
						onScrollTo();
					}
				},
				{
					rootMargin,
					threshold,
				},
			);

			observerRef.current.observe(element);
		},
		[onScrollTo, rootMargin, threshold],
	);

	return refCallback;
};
